import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

// Session 7.5 — accept either a supplier filter (resolves to all vendor
// brands the supplier carries) or a single vendor filter. The PO wizard
// passes whichever matches its recipient mode so the picker can't add items
// outside the PO's scope.
export interface ProductSearchScope {
  supplier?: string | null
  vendor?: string | null
}

// Browse mode (empty query, scope set) returns the whole in-scope catalog so
// the picker can group it by product title. Cap it so a huge brand
// (Schockemöhle ~3,400 variants) doesn't dump a multi-MB payload — the picker
// shows a "type to narrow" hint past the cap. A typed search is already
// narrow, so it uses a smaller limit.
const BROWSE_LIMIT = 1500
const SEARCH_LIMIT = 200

export function useProductSearch(query: string, scope: ProductSearchScope = {}) {
  const trimmed = query.trim()
  const supplierClean = (scope.supplier ?? '').trim() || undefined
  const vendorClean = (scope.vendor ?? '').trim() || undefined
  const hasScope = Boolean(supplierClean || vendorClean)
  const isSearch = trimmed.length >= 1
  const limit = isSearch ? SEARCH_LIMIT : BROWSE_LIMIT
  return useQuery({
    queryKey: [
      'products',
      { search: isSearch ? trimmed : '', supplier: supplierClean, vendor: vendorClean, limit },
    ] as const,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/inventory/products', {
        params: {
          query: {
            ...(isSearch ? { search: trimmed } : {}),
            limit,
            ...(supplierClean ? { supplier: supplierClean } : {}),
            ...(vendorClean && !supplierClean ? { vendor: vendorClean } : {}),
          },
        },
      })
      if (error) throw new Error('Product search failed')
      return data ?? []
    },
    // Browse needs a scope (so we never dump the whole catalog); a free-text
    // search is allowed even without a scope.
    enabled: hasScope || isSearch,
    staleTime: 30_000,
  })
}
